import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plane, Users, Calendar, Clock, MapPin, Plus, Trash2, 
  CheckCircle, Copy, AlertCircle, ArrowRight, Building2,
  User, Mail, Phone, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Common airlines for quick selection
const COMMON_AIRLINES = [
  { code: 'BA', name: 'British Airways' },
  { code: 'EK', name: 'Emirates' },
  { code: 'QR', name: 'Qatar Airways' },
  { code: 'EY', name: 'Etihad Airways' },
  { code: 'TK', name: 'Turkish Airlines' },
  { code: 'LH', name: 'Lufthansa' },
  { code: 'AF', name: 'Air France' },
  { code: 'KL', name: 'KLM' },
  { code: 'SV', name: 'Saudi Arabian Airlines' },
  { code: 'GF', name: 'Gulf Air' },
  { code: 'WY', name: 'Oman Air' },
  { code: 'MS', name: 'EgyptAir' },
  { code: 'RJ', name: 'Royal Jordanian' },
  { code: 'PK', name: 'Pakistan International Airlines' },
  { code: 'AI', name: 'Air India' },
  { code: '6E', name: 'IndiGo' },
  { code: 'SQ', name: 'Singapore Airlines' },
  { code: 'CX', name: 'Cathay Pacific' },
  { code: 'MH', name: 'Malaysia Airlines' },
  { code: 'TG', name: 'Thai Airways' },
];

const BOOKING_CLASSES = [
  { code: 'Y', name: 'Economy (Y)' },
  { code: 'W', name: 'Premium Economy (W)' },
  { code: 'J', name: 'Business (J)' },
  { code: 'C', name: 'Business (C)' },
  { code: 'F', name: 'First (F)' },
  { code: 'B', name: 'Economy (B)' },
  { code: 'H', name: 'Economy (H)' },
  { code: 'K', name: 'Economy (K)' },
  { code: 'M', name: 'Economy (M)' },
  { code: 'L', name: 'Economy (L)' },
  { code: 'Q', name: 'Economy (Q)' },
  { code: 'T', name: 'Economy (T)' },
  { code: 'V', name: 'Economy (V)' },
];

const AgentBooking = () => {
  // Flight segments state
  const [segments, setSegments] = useState([{
    id: 1,
    airlineCode: '',
    flightNumber: '',
    departureAirport: '',
    arrivalAirport: '',
    departureDate: '',
    departureTime: '',
    arrivalDate: '',
    arrivalTime: '',
    bookingClass: 'Y'
  }]);

  // Passengers state
  const [passengers, setPassengers] = useState([{
    id: 1,
    type: 'ADT',
    title: 'Mr',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'Male'
  }]);

  // Contact state
  const [contact, setContact] = useState({
    email: '',
    phone: ''
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [step, setStep] = useState(1); // 1: Entry, 2: Review, 3: Confirmation

  // Add segment
  const addSegment = () => {
    setSegments([...segments, {
      id: segments.length + 1,
      airlineCode: '',
      flightNumber: '',
      departureAirport: '',
      arrivalAirport: '',
      departureDate: '',
      departureTime: '',
      arrivalDate: '',
      arrivalTime: '',
      bookingClass: 'Y'
    }]);
  };

  // Remove segment
  const removeSegment = (id) => {
    if (segments.length > 1) {
      setSegments(segments.filter(s => s.id !== id));
    }
  };

  // Update segment
  const updateSegment = (id, field, value) => {
    setSegments(segments.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  // Add passenger
  const addPassenger = () => {
    setPassengers([...passengers, {
      id: passengers.length + 1,
      type: 'ADT',
      title: 'Mr',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'Male'
    }]);
  };

  // Remove passenger
  const removePassenger = (id) => {
    if (passengers.length > 1) {
      setPassengers(passengers.filter(p => p.id !== id));
    }
  };

  // Update passenger
  const updatePassenger = (id, field, value) => {
    setPassengers(passengers.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // Validate form
  const validateForm = () => {
    // Check segments
    for (const seg of segments) {
      if (!seg.airlineCode || !seg.flightNumber || !seg.departureAirport || 
          !seg.arrivalAirport || !seg.departureDate || !seg.departureTime) {
        return 'Please fill in all flight segment details';
      }
    }
    
    // Check passengers
    for (const pax of passengers) {
      if (!pax.firstName || !pax.lastName) {
        return 'Please fill in all passenger names';
      }
    }
    
    // Check contact
    if (!contact.email || !contact.phone) {
      return 'Please provide contact email and phone';
    }
    
    return null;
  };

  // Handle review
  const handleReview = () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep(2);
  };

  // Handle submit booking
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build segment details for Sabre PNR
      const segmentDetails = segments.map(seg => ({
        departure_airport: seg.departureAirport.toUpperCase(),
        arrival_airport: seg.arrivalAirport.toUpperCase(),
        departure_time: `${seg.departureDate}T${seg.departureTime}:00`,
        arrival_time: `${seg.arrivalDate || seg.departureDate}T${seg.arrivalTime || seg.departureTime}:00`,
        marketing_airline: seg.airlineCode.toUpperCase(),
        flight_number: seg.flightNumber,
        booking_class: seg.bookingClass
      }));

      // Build flight data
      const firstSeg = segments[0];
      const lastSeg = segments[segments.length - 1];
      
      const flightData = {
        from: firstSeg.departureAirport.toUpperCase(),
        to: lastSeg.arrivalAirport.toUpperCase(),
        origin: firstSeg.departureAirport.toUpperCase(),
        destination: lastSeg.arrivalAirport.toUpperCase(),
        departure_time: `${firstSeg.departureDate}T${firstSeg.departureTime}:00`,
        arrival_time: `${lastSeg.arrivalDate || lastSeg.departureDate}T${lastSeg.arrivalTime || lastSeg.departureTime}:00`,
        airline: firstSeg.airlineCode.toUpperCase(),
        airline_code: firstSeg.airlineCode.toUpperCase(),
        flight_number: `${firstSeg.airlineCode}${firstSeg.flightNumber}`,
        booking_class: firstSeg.bookingClass,
        cabin_class: firstSeg.bookingClass,
        stops: segments.length - 1,
        is_direct: segments.length === 1,
        source: 'sabre', // Mark as Sabre source for real PNR creation
        segments: segmentDetails
      };

      // Build passenger data
      const passengerData = passengers.map(pax => ({
        type: pax.type,
        title: pax.title,
        first_name: pax.firstName,
        last_name: pax.lastName,
        date_of_birth: pax.dateOfBirth || null,
        gender: pax.gender,
        email: contact.email,
        phone: contact.phone
      }));

      // Submit booking - use NDC endpoint for Sabre flights with fallback
      const bookingEndpoint = flightData.source === 'sabre' 
        ? `${API_URL}/api/bookings/create-ndc` 
        : `${API_URL}/api/bookings/create`;
      
      const response = await axios.post(bookingEndpoint, {
        flight_id: `manual_${Date.now()}`,
        flight_data: {
          ...flightData,
          offer_id: flightData.offer_id || flightData.id,
          offer_items: flightData.offer_items || [{ id: "1" }]
        },
        passengers: passengerData,
        contact: {
          email: contact.email,
          phone: contact.phone
        },
        passenger_counts: {
          adults: passengers.filter(p => p.type === 'ADT').length,
          children: passengers.filter(p => p.type === 'CNN').length,
          infants: passengers.filter(p => p.type === 'INF').length
        },
        total_price: 0, // Agent will handle pricing
        currency: 'GBP'
      });

      setBookingResult(response.data);
      setStep(3);
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.response?.data?.message || 'Failed to create PNR. Please check flight details and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy PNR to clipboard with fallback
  const copyPNR = async () => {
    const text = bookingResult?.pnr;
    if (!text) {
      toast.error('No PNR to copy');
      return;
    }
    
    try {
      // Try modern clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for HTTP or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
        } catch (execErr) {
          console.warn('execCommand failed:', execErr);
        }
        
        document.body.removeChild(textArea);
      }
      
      toast.success('PNR copied to clipboard!', {
        description: text,
        duration: 3000,
      });
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast.info(`Copy this PNR manually: ${text}`, {
        duration: 10000
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setSegments([{
      id: 1,
      airlineCode: '',
      flightNumber: '',
      departureAirport: '',
      arrivalAirport: '',
      departureDate: '',
      departureTime: '',
      arrivalDate: '',
      arrivalTime: '',
      bookingClass: 'Y'
    }]);
    setPassengers([{
      id: 1,
      type: 'ADT',
      title: 'Mr',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'Male'
    }]);
    setContact({ email: '', phone: '' });
    setBookingResult(null);
    setError(null);
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
            <Building2 className="h-4 w-4" />
            <span className="text-sm font-medium">Agent Portal</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Manual Flight Entry</h1>
          <p className="text-slate-600">Create Booking with manual flight details</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-purple-600' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Entry */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Flight Segments */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-3">
                  <Plane className="h-5 w-5" />
                  Flight Segments
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {segments.map((segment, index) => (
                  <div key={segment.id} className="p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-slate-700">Segment {index + 1}</h4>
                      {segments.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeSegment(segment.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Airline */}
                      <div>
                        <Label className="text-xs text-slate-500">Airline Code *</Label>
                        <Select 
                          value={segment.airlineCode} 
                          onValueChange={(v) => updateSegment(segment.id, 'airlineCode', v)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {COMMON_AIRLINES.map(a => (
                              <SelectItem key={a.code} value={a.code}>
                                {a.code} - {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Flight Number */}
                      <div>
                        <Label className="text-xs text-slate-500">Flight # *</Label>
                        <Input 
                          value={segment.flightNumber}
                          onChange={(e) => updateSegment(segment.id, 'flightNumber', e.target.value)}
                          placeholder="123"
                          className="mt-1"
                        />
                      </div>

                      {/* Booking Class */}
                      <div>
                        <Label className="text-xs text-slate-500">Class *</Label>
                        <Select 
                          value={segment.bookingClass} 
                          onValueChange={(v) => updateSegment(segment.id, 'bookingClass', v)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BOOKING_CLASSES.map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Origin */}
                      <div>
                        <Label className="text-xs text-slate-500">From (Airport Code) *</Label>
                        <Input 
                          value={segment.departureAirport}
                          onChange={(e) => updateSegment(segment.id, 'departureAirport', e.target.value.toUpperCase())}
                          placeholder="LHR"
                          maxLength={3}
                          className="mt-1 uppercase"
                        />
                      </div>

                      {/* Destination */}
                      <div>
                        <Label className="text-xs text-slate-500">To (Airport Code) *</Label>
                        <Input 
                          value={segment.arrivalAirport}
                          onChange={(e) => updateSegment(segment.id, 'arrivalAirport', e.target.value.toUpperCase())}
                          placeholder="DXB"
                          maxLength={3}
                          className="mt-1 uppercase"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Departure Date */}
                      <div>
                        <Label className="text-xs text-slate-500">Dep Date *</Label>
                        <Input 
                          type="date"
                          value={segment.departureDate}
                          onChange={(e) => updateSegment(segment.id, 'departureDate', e.target.value)}
                          className="mt-1"
                        />
                      </div>

                      {/* Departure Time */}
                      <div>
                        <Label className="text-xs text-slate-500">Dep Time *</Label>
                        <Input 
                          type="time"
                          value={segment.departureTime}
                          onChange={(e) => updateSegment(segment.id, 'departureTime', e.target.value)}
                          className="mt-1"
                        />
                      </div>

                      {/* Arrival Date */}
                      <div>
                        <Label className="text-xs text-slate-500">Arr Date</Label>
                        <Input 
                          type="date"
                          value={segment.arrivalDate}
                          onChange={(e) => updateSegment(segment.id, 'arrivalDate', e.target.value)}
                          className="mt-1"
                        />
                      </div>

                      {/* Arrival Time */}
                      <div>
                        <Label className="text-xs text-slate-500">Arr Time</Label>
                        <Input 
                          type="time"
                          value={segment.arrivalTime}
                          onChange={(e) => updateSegment(segment.id, 'arrivalTime', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button 
                  variant="outline" 
                  onClick={addSegment}
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Segment (Connection/Return)
                </Button>
              </CardContent>
            </Card>

            {/* Passengers */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-3">
                  <Users className="h-5 w-5" />
                  Passengers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {passengers.map((pax, index) => (
                  <div key={pax.id} className="p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-slate-700">Passenger {index + 1}</h4>
                      {passengers.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removePassenger(pax.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {/* Type */}
                      <div>
                        <Label className="text-xs text-slate-500">Type *</Label>
                        <Select 
                          value={pax.type} 
                          onValueChange={(v) => updatePassenger(pax.id, 'type', v)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ADT">Adult</SelectItem>
                            <SelectItem value="CNN">Child</SelectItem>
                            <SelectItem value="INF">Infant</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Title */}
                      <div>
                        <Label className="text-xs text-slate-500">Title *</Label>
                        <Select 
                          value={pax.title} 
                          onValueChange={(v) => updatePassenger(pax.id, 'title', v)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mr">Mr</SelectItem>
                            <SelectItem value="Mrs">Mrs</SelectItem>
                            <SelectItem value="Ms">Ms</SelectItem>
                            <SelectItem value="Miss">Miss</SelectItem>
                            <SelectItem value="Mstr">Mstr</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* First Name */}
                      <div>
                        <Label className="text-xs text-slate-500">First Name *</Label>
                        <Input 
                          value={pax.firstName}
                          onChange={(e) => updatePassenger(pax.id, 'firstName', e.target.value.toUpperCase())}
                          placeholder="JOHN"
                          className="mt-1 uppercase"
                        />
                      </div>

                      {/* Last Name */}
                      <div>
                        <Label className="text-xs text-slate-500">Last Name *</Label>
                        <Input 
                          value={pax.lastName}
                          onChange={(e) => updatePassenger(pax.id, 'lastName', e.target.value.toUpperCase())}
                          placeholder="DOE"
                          className="mt-1 uppercase"
                        />
                      </div>

                      {/* Gender */}
                      <div>
                        <Label className="text-xs text-slate-500">Gender</Label>
                        <Select 
                          value={pax.gender} 
                          onValueChange={(v) => updatePassenger(pax.id, 'gender', v)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}

                <Button 
                  variant="outline" 
                  onClick={addPassenger}
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Passenger
                </Button>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-3">
                  <Mail className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Email *</Label>
                    <Input 
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContact({...contact, email: e.target.value})}
                      placeholder="customer@email.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone *</Label>
                    <Input 
                      value={contact.phone}
                      onChange={(e) => setContact({...contact, phone: e.target.value})}
                      placeholder="+44 7XXX XXXXXX"
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            )}

            {/* Submit */}
            <Button 
              onClick={handleReview}
              className="w-full h-14 text-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              Review & Create PNR
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-t-lg">
                <CardTitle>Review Booking Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Segments Summary */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Plane className="h-4 w-4" /> Flight Segments
                  </h4>
                  {segments.map((seg, i) => (
                    <div key={seg.id} className="bg-slate-50 p-4 rounded-lg mb-2">
                      <div className="flex items-center gap-4">
                        <span className="font-bold text-blue-600">{seg.airlineCode}{seg.flightNumber}</span>
                        <span className="text-slate-600">{seg.departureAirport}</span>
                        <ArrowRight className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-600">{seg.arrivalAirport}</span>
                        <span className="text-sm text-slate-500">
                          {seg.departureDate} {seg.departureTime}
                        </span>
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                          Class {seg.bookingClass}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Passengers Summary */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Passengers ({passengers.length})
                  </h4>
                  {passengers.map((pax, i) => (
                    <div key={pax.id} className="flex items-center gap-4 py-2">
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-medium">
                        {pax.type}
                      </span>
                      <span className="text-slate-700">{pax.title} {pax.firstName} {pax.lastName}</span>
                    </div>
                  ))}
                </div>

                {/* Contact Summary */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Contact
                  </h4>
                  <div className="text-slate-600">
                    <p>{contact.email}</p>
                    <p>{contact.phone}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            )}

            <div className="flex gap-4">
              <Button 
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-14"
              >
                Back to Edit
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 h-14 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating PNR...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Create Booking
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && bookingResult && (
          <div className="space-y-6">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-cyan-600 p-8 text-center text-white">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-12 w-12" />
                </div>
                <h2 className="text-3xl font-bold mb-2">PNR Created!</h2>
                <p className="text-emerald-100">Booking has been confirmed in Airline System</p>
              </div>

              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-8 text-center">
                <div className="text-sm text-slate-400 mb-2 uppercase tracking-wider">
                  Your PNR / Record Locator
                </div>
                <div className="text-6xl font-black text-white tracking-[0.2em] mb-4">
                  {bookingResult.pnr}
                </div>
                
                {bookingResult.pnr_source === 'sabre' ? (
                  <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full mb-4">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Real Airline System PNR - Verify in your GDS</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Local Reference - Demo Mode</span>
                  </div>
                )}

                <div className="flex justify-center gap-3">
                  <Button 
                    variant="outline"
                    onClick={copyPNR}
                    className="border-white/30 text-white hover:bg-white/20"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy PNR
                  </Button>
                </div>
              </div>

              <CardContent className="p-6">
                <p className="text-center text-slate-600 mb-6">
                  {bookingResult.message}
                </p>
                
                <Button 
                  onClick={resetForm}
                  variant="outline"
                  className="w-full"
                >
                  Create Another Booking
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentBooking;
